import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Baby, ClipboardList, StickyNote } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Checkbox, Field, Input, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { safeReturnTo } from '../opd/BookAppointmentPage';

interface DeliverySummary {
  id: string;
  deliveredAt: string | null;
  gestationWeeks: number | null;
  patient: { id: string; mrn: string; fullName: string };
}

export function CompleteDeliveryPage() {
  const { deliveryId } = useParams<{ deliveryId: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), `/maternity?delivery=${deliveryId}`);

  const delivery = useQuery({
    queryKey: ['delivery', deliveryId],
    queryFn: () => get<DeliverySummary>(`/maternity/deliveries/${deliveryId}`),
    enabled: Boolean(deliveryId),
  });

  if (!delivery.data) return <RecordState error={delivery.error} missing={delivery.isSuccess} />;
  return <CompleteDeliveryForm key={delivery.data.id} delivery={delivery.data} returnTo={returnTo} />;
}

function CompleteDeliveryForm({ delivery, returnTo }: { delivery: DeliverySummary; returnTo: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    mode: 'normal',
    outcome: 'live_birth',
    episiotomy: false,
    bloodLossMl: '',
    complications: '',
  });
  const [baby, setBaby] = useState({ sex: 'female', birthWeightG: '', apgar1: '', apgar5: '' });
  const [error, setError] = useState<string | null>(null);

  const hasNewborn = form.outcome === 'live_birth' || form.outcome === 'still_birth';

  const complete = useMutation({
    mutationFn: () =>
      post(`/maternity/deliveries/${delivery.id}/complete`, {
        mode: form.mode,
        outcome: form.outcome,
        episiotomy: form.episiotomy,
        bloodLossMl: form.bloodLossMl ? Number(form.bloodLossMl) : undefined,
        complications: form.complications || undefined,
        newborns: hasNewborn
          ? [
              {
                sex: baby.sex,
                birthWeightG: baby.birthWeightG ? Number(baby.birthWeightG) : undefined,
                apgar1: baby.apgar1 ? Number(baby.apgar1) : undefined,
                apgar5: baby.apgar5 ? Number(baby.apgar5) : undefined,
              },
            ]
          : [],
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['delivery', delivery.id] });
      void queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const dirty =
    !complete.isSuccess &&
    (form.mode !== 'normal' ||
      form.outcome !== 'live_birth' ||
      form.episiotomy ||
      form.bloodLossMl !== '' ||
      form.complications !== '' ||
      baby.sex !== 'female' ||
      baby.birthWeightG !== '' ||
      baby.apgar1 !== '' ||
      baby.apgar5 !== '');

  return (
    <FormScreen
      title="Record the delivery"
      description="A live birth needs at least one newborn record — the birth register depends on it"
      eyebrow={`${delivery.patient.fullName} · ${delivery.patient.mrn}`}
      icon={Baby}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        complete.mutate();
      }}
    >
      {delivery.deliveredAt && <Alert variant="warning">This delivery has already been recorded.</Alert>}

      <FormSection title={tr('ipd', 'outcome')} icon={ClipboardList}>
        <Field label="Mode" required>
          <ChoiceButtons<string>
            layout="chips"
            value={form.mode}
            onChange={(value) => setForm({ ...form, mode: value })}
            options={['normal', 'assisted', 'caesarean', 'vacuum', 'forceps'].map((m) => ({ value: m, label: humanize(m, 'delivery_mode') }))}
          />
        </Field>
        <Field label="Outcome" required>
          <ChoiceButtons<string>
            layout="chips"
            value={form.outcome}
            onChange={(value) => setForm({ ...form, outcome: value })}
            options={['live_birth', 'still_birth', 'neonatal_death', 'abortion'].map((o) => ({ value: o, label: humanize(o, 'delivery_outcome') }))}
          />
        </Field>
        <Field label="Blood loss (ml)">
          <Input type="number" min={0} value={form.bloodLossMl} onChange={(e) => setForm({ ...form, bloodLossMl: e.target.value })} />
        </Field>
        <Checkbox checked={form.episiotomy} onCheckedChange={(checked) => setForm({ ...form, episiotomy: checked })} label="Episiotomy" className="self-end" />
      </FormSection>

      {hasNewborn && (
        <FormSection title={tr('ipd', 'newborn')} icon={Baby} columns={3}>
          <Field label="Sex">
            <ChoiceButtons<string>
              layout="chips"
              value={baby.sex}
              onChange={(value) => setBaby({ ...baby, sex: value })}
              options={['female', 'male', 'other', 'unknown'].map((s) => ({ value: s, label: humanize(s, 'sex') }))}
            />
          </Field>
          <Field label="Weight (g)">
            <Input type="number" value={baby.birthWeightG} onChange={(e) => setBaby({ ...baby, birthWeightG: e.target.value })} />
          </Field>
          <Field label="APGAR 1 min">
            <Input type="number" min={0} max={10} value={baby.apgar1} onChange={(e) => setBaby({ ...baby, apgar1: e.target.value })} />
          </Field>
          <Field label="APGAR 5 min">
            <Input type="number" min={0} max={10} value={baby.apgar5} onChange={(e) => setBaby({ ...baby, apgar5: e.target.value })} />
          </Field>
        </FormSection>
      )}

      <FormSection title={tr('ipd', 'notes')} icon={StickyNote} columns={1}>
        <Field label="Complications">
          <Textarea rows={2} value={form.complications} onChange={(e) => setForm({ ...form, complications: e.target.value })} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Record" loading={complete.isPending} cancelTo={returnTo} />
    </FormScreen>
  );
}
